import { motion } from 'framer-motion';
import SectionHeader from '../components/SectionHeader';
import AnimatedCounter from '../components/AnimatedCounter';

const milestones = [
  { year: '2023', event: 'Avdhoot Group founded in Burhanpur with a vision to build quality homes.' },
  { year: '2024', event: 'Launched Rameshwar Dham and Kesar Bagh, serving families with premium homes.' },
  { year: '2025', event: 'Completed Dev Vatika and crossed 5+ projects with 27+ happy families.' },
];

const team = [
  {
    name: 'Devendra Choudhary',
    role: 'CEO & Founder',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80',
  },
  {
    name: 'Pooja Devendra Choudhary',
    role: 'Co-Founder',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80',
  },
];

const stats = [
  { number: 3, suffix: '+', label: 'Years of Experience' },
  { number: 5, suffix: '+', label: 'Projects Completed' },
  { number: 27, suffix: '+', label: 'Happy Families' },
];

const About = () => {
  return (
    <div>
      {/* Page Header */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80"
            alt="About Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-charcoal/75" />
        </div>
        <div className="relative z-10 text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-cream-200/40 font-sans text-xs uppercase tracking-widest-plus mb-4"
          >
            Home &nbsp;/&nbsp; About
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4"
          >
            About Us
          </motion.h1>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="gold-divider mx-auto mb-4"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-cream-200/60 font-sans text-lg"
          >
            Building trust, one home at a time
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-28 lg:py-32 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <SectionHeader
                label="Our Story"
                title="A Legacy of Trust & Quality"
                align="left"
              />
              <p className="text-charcoal/60 font-sans leading-relaxed mb-6 mt-6">
                Founded in 2023, Avdhoot Group began with a simple vision: to build homes that families would cherish for generations. Starting with our first residential project in Burhanpur, we quickly earned a reputation for uncompromising quality and transparent dealings.
              </p>
              <p className="text-charcoal/60 font-sans leading-relaxed mb-6">
                Over the past 3+ years, we have grown from a small local builder to Burhanpur's most trusted real estate developer. Our portfolio now includes premium apartments and luxury villas — all bearing the hallmark of Avdhoot Group's excellence.
              </p>
              <p className="text-charcoal/60 font-sans leading-relaxed">
                We are proud to have delivered 5+ projects and helped over 27 families find their dream homes. Our success is built on a foundation of trust, innovation, and an unwavering commitment to our customers' happiness.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                {/* Decorative gold frame */}
                <div className="absolute -top-4 -right-4 w-2/3 h-2/3 border border-gold/20 hidden lg:block" />
                <img
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80"
                  alt="Project 1"
                  className="w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80"
                  alt="Project 2"
                  className="w-full h-48 object-cover mt-8"
                />
                <img
                  src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80"
                  alt="Project 3"
                  className="w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600&q=80"
                  alt="Project 4"
                  className="w-full h-48 object-cover mt-8"
                />
              </div>
              {/* Dot pattern element */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 dot-pattern opacity-10 hidden lg:block" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-28 lg:py-32 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="card-premium p-10 border-t-2 border-gold"
            >
              <div className="w-14 h-14 bg-gold/10 text-gold flex items-center justify-center mb-6 group-hover:bg-gold group-hover:text-charcoal transition-all duration-500">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-serif font-semibold text-charcoal mb-4">Our Vision</h3>
              <p className="text-charcoal/60 font-sans leading-relaxed">
                To be the most trusted and preferred real estate developer in Central India, known for creating sustainable, innovative, and premium living spaces that enhance the quality of life for every homeowner.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="card-premium p-10 border-t-2 border-gold"
            >
              <div className="w-14 h-14 bg-gold/10 text-gold flex items-center justify-center mb-6">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-serif font-semibold text-charcoal mb-4">Our Mission</h3>
              <p className="text-charcoal/60 font-sans leading-relaxed">
                To deliver exceptional real estate projects with a focus on quality construction, timely delivery, and customer satisfaction. We aim to transform Burhanpur's landscape while making premium living accessible to every family.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-20 bg-charcoal shimmer-bg">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 lg:gap-12">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="text-center"
              >
                <div className="w-8 h-0.5 bg-gold/40 mx-auto mb-6" />
                <p className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gradient-gold mb-2">
                  <AnimatedCounter target={stat.number} suffix={stat.suffix} />
                </p>
                <p className="text-cream-200/40 font-sans text-xs uppercase tracking-widest-plus">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-28 lg:py-32 bg-cream-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-20">
            <SectionHeader
              label="Our Journey"
              title="Key Milestones"
            />
          </div>

          <div className="relative">
            {/* Vertical connecting line */}
            <div className="absolute left-10 top-0 bottom-0 w-px bg-gold/20" />

            <div className="space-y-12">
              {milestones.map((milestone, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-start space-x-8 relative"
                >
                  {/* Dot connector */}
                  <div className="absolute left-10 top-5 w-3 h-3 -translate-x-1/2 rounded-full bg-gold border-4 border-cream-50 z-10" />

                  <div className="shrink-0 w-20 h-20 bg-charcoal border border-gold/20 text-white flex items-center justify-center">
                    <span className="text-sm font-serif font-bold text-gradient-gold">{milestone.year}</span>
                  </div>
                  <div className="pt-3">
                    <p className="text-charcoal/60 font-sans leading-relaxed">{milestone.event}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-28 lg:py-32 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-20">
            <SectionHeader
              label="Our Team"
              title="The People Behind Avdhoot Group"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {team.map((member, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="group"
              >
                <div className="relative overflow-hidden mb-5">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700 ease-luxury"
                  />
                  {/* Hover overlay with contact icons */}
                  <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/50 transition-all duration-500 ease-luxury flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                    <span className="w-10 h-10 border border-white/40 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </span>
                    <span className="w-10 h-10 border border-white/40 flex items-center justify-center text-white hover:border-gold hover:text-gold transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                  </div>
                </div>
                <h4 className="text-lg font-serif font-semibold text-charcoal">{member.name}</h4>
                <p className="text-gold font-sans text-xs uppercase tracking-widest-plus mt-1">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
